import { useEffect, useRef, useState } from 'react';
import { mountAgeGate, type AgeGateArtUrls } from '@goodtime/engine';
import borderUrl from '../assets/brand/border_forest.png';
import backgroundUrl from '../assets/environment/forest.png';
import coinUrl from '../assets/icon/coin.png';
import columnUrl from '../assets/reel/forest_column_bg.png';
import { FOREST_THEME } from '../themes/forestTheme';
import { BUTTON_PLATES } from '../themes/roadGames';

/**
 * First-launch age check (producer 2026-10-12: add it, even though it is
 * friction). Goodtime is free entertainment for adults: just for fun, with
 * nothing to win or cash out; players under 18 are turned away.
 *
 * <p>The gate itself is drawn in Pixi on the board kit (engine
 * `widgets/agegate`), over the first slot playing itself. It asks one
 * question, "Are you 18 or over?", answered with one tap (producer
 * 2026-11-03: the birth year on a keypad stopped new players before their
 * first spin). Only the answer is stored on the device
 * ({@link AGE_CHECK_KEY}: "ok" or "under").
 */
export const AGE_CHECK_KEY = 'goodtime.ageCheck';
export type AgeCheck = 'ok' | 'under' | null;

export function readAgeCheck(): AgeCheck {
  try {
    const v = localStorage.getItem(AGE_CHECK_KEY);
    return v === 'ok' || v === 'under' ? v : null;
  } catch {
    return null;
  }
}

function writeAgeCheck(v: 'ok' | 'under'): void {
  try {
    localStorage.setItem(AGE_CHECK_KEY, v);
  } catch {
    /* private mode: the check simply asks again next time */
  }
}

/** Whispering Woods, the slot a first visit opens: its backdrop, frame and
 *  symbols (the fox, owl and deer last, as the ones that line up). */
const slot = FOREST_THEME.assets?.slot ?? {};
const GATE_ART: AgeGateArtUrls = {
  background: backgroundUrl,
  column: columnUrl,
  border: borderUrl,
  coin: coinUrl,
  symbols: ['cherry', 'lemon', 'orange', 'bell', 'star', 'scatter', 'seven', 'gem', 'money']
    .map((id) => slot[id]?.src)
    .filter((src): src is string => typeof src === 'string'),
};

export interface AgeGateProps {
  /** Null on a first visit; "under" if this device was turned away before. */
  state: AgeCheck;
  /** The question is on screen (once; not for a device turned away before), so the funnel counts who stops at it. */
  onShown?: () => void;
  onResult: (result: 'ok' | 'under') => void;
}

export function AgeGate({ state, onShown, onResult }: AgeGateProps) {
  const host = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);
  const report = useRef(onResult);
  report.current = onResult;
  const startedUnder = useRef(state === 'under');
  const shown = useRef(onShown);

  useEffect(() => {
    if (!host.current) return;
    return mountAgeGate(host.current, {
      state: startedUnder.current ? 'under' : 'ask',
      art: GATE_ART,
      plates: BUTTON_PLATES,
      onReady: () => {
        setDrawn(true);
        if (!startedUnder.current) shown.current?.();
      },
      onResult: (result) => {
        writeAgeCheck(result);
        report.current(result);
      },
    });
  }, []);

  return (
    <div
      ref={host}
      role="dialog"
      aria-modal="true"
      aria-label="Age check"
      data-testid="age-gate"
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: drawn ? 'transparent' : '#140d05' }}
    />
  );
}
